import {
  Card,
  CardButton,
  ConditionalWrapper,
  Divider,
  IncrementableNumberInput,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';

import Tag from '@payaca/components/tag/Tag';
import AppearTransition from '@payaca/components/transitions/AppearTransition';
import { CurrencyValue as TCurrencyValue } from '@payaca/types/currencyTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';

type OwnProps = {
  name: string;
  price?: TCurrencyValue;
  tags?: { name: string; colour?: string | null }[];
  thumbnailUrl?: string;
  childrenClassName?: string;
  children?: React.ReactNode;
};

export type MaterialCardWithQuantityProps = OwnProps & {
  quantity: number;
  onChangeQuantity: (quantity: number) => void;
  maxQuantity?: number;
  /** When true, the quantity input renders as disabled. */
  quantityDisabled?: boolean;
};

export type MaterialCardProps =
  | OwnProps
  | (OwnProps & {
      actionOnClick: () => void;
      actionDisabled?: boolean;
    })
  | MaterialCardWithQuantityProps;

export type MaterialCardPropsWithTransition = MaterialCardProps & {
  afterLeave: () => void;
  show: boolean;
};

export const MaterialCardWithTransition: FC<MaterialCardPropsWithTransition> = (
  props
) => {
  const { afterLeave, show, ...rest } = props;

  return (
    <AppearTransition show={show} afterLeave={afterLeave}>
      <MaterialCard {...rest} />
    </AppearTransition>
  );
};

const MaterialCard: FC<MaterialCardProps> = (props) => {
  const { name, price, tags, thumbnailUrl, childrenClassName, children } =
    props;

  const wrapperProps = {
    onClick: 'actionOnClick' in props ? props.actionOnClick : undefined,
    disabled: 'actionDisabled' in props ? props.actionDisabled : undefined,
  };

  return (
    <ConditionalWrapper
      condition={'actionOnClick' in props}
      Wrapper={CardButton}
      FallbackWrapper={Card}
      wrapperProps={wrapperProps}
      fallbackWrapperProps={wrapperProps}
    >
      <Card.Body className="space-y-3.5">
        <div className="flex gap-3.5">
          <div className="w-14">
            {thumbnailUrl ? (
              <img
                className="overflow-hidden rounded"
                src={thumbnailUrl}
                alt={name}
              />
            ) : (
              <UntitledIcon
                name="image-01.3"
                vectorEffect="non-scaling-stroke"
                className="h-10 w-10 text-gray-700"
              />
            )}
          </div>

          <div className="flex flex-auto flex-col items-end gap-2">
            <div className="flex justify-between gap-3.5 w-full">
              <h4>{name}</h4>

              {price ? <CurrencyValue {...price} /> : '-'}
            </div>

            <div className="flex flex-wrap gap-1.5 justify-end">
              {tags?.map((tag) => (
                <Tag key={tag.name} colour={tag.colour || 'black'}>
                  {tag.name}
                </Tag>
              ))}
            </div>
          </div>
        </div>

        <Divider />

        <div className="flex flex-wrap items-center justify-between">
          <div className={childrenClassName}>{children}</div>

          {'quantity' in props && (
            <IncrementableNumberInput
              sizeVariant="sm"
              wrapperClassName="max-w-32"
              value={props.quantity}
              onChange={(value) => props.onChangeQuantity(value ?? 0)}
              min={0}
              max={props.maxQuantity}
              disabled={props.quantityDisabled}
            />
          )}
        </div>
      </Card.Body>
    </ConditionalWrapper>
  );
};

export default MaterialCard;
